import { inject, Injectable, Injector } from '@angular/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { RegistrationError } from '@capacitor/push-notifications/dist/esm/definitions';
import { combineLatest, from, Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PUSH_NOTIFICATION_OPTIONS } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { Capacitor } from '@capacitor/core';
import { map } from 'rxjs/operators';
import { DeviceIdService } from '../device-id-service/device-id.service';
import { ToastService } from '../toast-service/toast.service';
import { TranslationService } from '../translate-service/translation.service';
import { PushNotificationEventsService } from './push-notification-events.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private readonly options = inject(PUSH_NOTIFICATION_OPTIONS, { optional: true }) ?? { isEnabled: true };
    private readonly deviceIdService = inject(DeviceIdService);
    private readonly toastService = inject(ToastService);
    /**
     * Resolved lazily to break a construction-time DI cycle: `ApplicationContextStore.withHooks`
     * injects `PushNotificationService`, which would in turn inject `TranslationService`, which
     * injects `ApplicationContextStore` -> cycle. Deferring the `TranslationService` lookup to the
     * point of use lets the store finish constructing first.
     */
    private readonly injector = inject(Injector);
    private readonly events = inject(PushNotificationEventsService);

    /**
     * Guards against duplicate listener attachment. `initialize()` is invoked from both
     * `AuthService` (login success) and `ApplicationContextStore` (store init when already logged
     * in). Without this flag, a re-login without app restart would stack listeners and each push
     * would fire the handlers N times.
     */
    private initialized = false;

    initialize(): void {
        if (this.initialized) {
            return;
        }
        if (Capacitor.getPlatform() === 'web' || !this.options.isEnabled) {
            return;
        }
        this.initialized = true;

        PushNotifications.requestPermissions().then((result) => {
            if (result.receive === 'granted') {
                PushNotifications.register();
            } else {
                const translationService = this.injector.get(TranslationService);
                this.toastService.showError(translationService.getTranslation('Common', 'PushNotification', 'Permission denied'));
            }
        });

        PushNotifications.addListener('registration', (token: Token) => {
            this.registerDeviceToken(token.value).subscribe();
        });

        PushNotifications.addListener('registrationError', (error: RegistrationError) => {
            console.error('Push notification registration error: ' + error.error);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
            this.events.emitReceived(notification);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
            this.events.emitActionPerformed(notification);
        });
    }

    private registerDeviceToken(deviceToken: string): Observable<string> {
        if (deviceToken === null || deviceToken === undefined || deviceToken === '') return of('');

        return combineLatest([from(this.deviceIdService.getDeviceId()), from(this.deviceIdService.getDeviceInfo())]).pipe(
            switchMap(([deviceId, deviceInfo]) =>
                this.http.post<{ message: string }>(this.serviceUtils.getBaseUrl('notification') + '/register-push-device', {
                    deviceId,
                    deviceToken: deviceToken,
                    platform: deviceInfo.platform,
                    deviceModel: deviceInfo.model,
                    deviceModelDisplay: deviceInfo.name,
                }),
            ),
            map(() => deviceToken),
        );
    }
}
